import { useState } from 'react'
import { useAddCode, useEditCode, useRemoveCode } from 'src/hooks/useAffiliate'
import { AffiliateMe, CodeStats } from 'src/types/Affiliate'
import { formatCents } from 'src/utils/affiliateFormat'
import { copyText } from 'src/utils/clipboard'
import { notify } from 'src/utils/notify'
import { errorMessage } from './labels'
import {
    ActionButton,
    Badge,
    Card,
    CardTitle,
    Checkbox,
    CheckboxInput,
    EmptyRow,
    ErrorText,
    Input,
    Label,
    LinkButton,
    Muted,
    Row,
    SecondaryButton,
    Spacer,
    Table,
    TableWrap,
} from './ui'

const CODE_PATTERN = /^[a-z0-9-]{3,20}$/
const DEFAULT_TRIAL_DAYS = 7

const copyLink = async (url: string) => {
    try {
        await copyText(url)
        notify('Link copied', 'success')
    } catch {
        notify("Couldn't copy. Select the link and copy it manually.", 'error')
    }
}

const AddCodeForm = ({ trialDays, onDone }: { trialDays: number; onDone: () => void }) => {
    const addCode = useAddCode()
    const [code, setCode] = useState('')
    const [label, setLabel] = useState('')
    const [trial, setTrial] = useState(false)
    const normalized = code.trim().toLowerCase()
    const valid = CODE_PATTERN.test(normalized)

    const submit = () =>
        addCode.mutate(
            { code: normalized, label: label.trim() || undefined, trial },
            {
                onSuccess: () => {
                    notify(`Code ${normalized} added`, 'success')
                    onDone()
                },
            }
        )

    return (
        <Card style={{ background: 'rgba(255,255,255,0.04)' }}>
            <Row $gap={12}>
                <Label style={{ flex: '1 1 180px' }}>
                    Code
                    <Input
                        value={code}
                        maxLength={20}
                        placeholder="joe-tiktok"
                        onChange={(e) => setCode(e.target.value.toLowerCase())}
                        aria-invalid={!!code && !valid}
                    />
                </Label>
                <Label style={{ flex: '1 1 180px' }}>
                    Where you'll share it (optional)
                    <Input value={label} maxLength={40} placeholder="TikTok" onChange={(e) => setLabel(e.target.value)} />
                </Label>
            </Row>
            <Checkbox>
                <input type="checkbox" checked={trial} onChange={(e) => setTrial(e.target.checked)} />
                <span>Give new customers a {trialDays}-day free trial through this code</span>
            </Checkbox>
            <Muted>3–20 characters: lowercase letters, numbers and dashes. Codes can't be reused once taken.</Muted>
            {addCode.isError && <ErrorText>{errorMessage(addCode.error)}</ErrorText>}
            <Row>
                <ActionButton disabled={!valid || addCode.isPending} onClick={submit}>
                    Add code
                </ActionButton>
                <SecondaryButton onClick={onDone}>Cancel</SecondaryButton>
            </Row>
        </Card>
    )
}

const CodeRow = ({ code, canEdit, trialDays }: { code: CodeStats; canEdit: boolean; trialDays: number }) => {
    const editCode = useEditCode()
    const removeCode = useRemoveCode()
    const retired = code.kind === 'retired'

    const toggleTrial = (trial: boolean) =>
        editCode.mutate(
            { code: code.code, trial },
            {
                onSuccess: () =>
                    notify(trial ? `${code.code} now gives a ${trialDays}-day free trial` : `Trial removed from ${code.code}`, 'success'),
                onError: (error) => notify(errorMessage(error), 'error'),
            }
        )

    const remove = () => {
        if (
            !window.confirm(
                `Remove ${code.code}? Links you've already shared with it keep working and still credit you.`
            )
        )
            return
        removeCode.mutate(code.code, {
            onSuccess: () => notify(`${code.code} removed`, 'success'),
            onError: (error) => notify(errorMessage(error), 'error'),
        })
    }

    return (
        <tr style={retired ? { opacity: 0.6 } : undefined}>
            <td>
                <Row $gap={6} $wrap={false}>
                    <strong>{code.code}</strong>
                    {code.kind === 'primary' && <Badge $tone="pink">Main</Badge>}
                </Row>
                {code.label && <Muted style={{ fontSize: 12 }}>{code.label}</Muted>}
            </td>
            <td>
                {retired ? (
                    code.trial ? `${code.trialDays} days` : '—'
                ) : (
                    <CheckboxInput
                        aria-label={`Free trial through ${code.code}`}
                        checked={code.trial}
                        disabled={!canEdit || editCode.isPending}
                        onChange={(e) => toggleTrial(e.target.checked)}
                    />
                )}
            </td>
            <td className="num">{code.clicks.toLocaleString()}</td>
            <td className="num">{code.joined.toLocaleString()}</td>
            <td className="num">{code.paying.toLocaleString()}</td>
            <td className="num">{formatCents(code.earnedCents)}</td>
            <td className="actions">
                <Row $gap={12} $wrap={false}>
                    <LinkButton onClick={() => copyLink(code.shareUrl)}>Copy link</LinkButton>
                    {code.kind === 'campaign' && canEdit && (
                        <LinkButton onClick={remove} disabled={removeCode.isPending}>
                            Remove
                        </LinkButton>
                    )}
                </Row>
            </td>
        </tr>
    )
}

/**
 * The affiliate's codes and how each performs. A code per place they share (TikTok, a Discord server…) shows which
 * one brings buyers; a code can also give new customers a free trial.
 */
const CodesCard = ({
    me,
    codes,
    loading,
    rangeText,
}: {
    me: AffiliateMe
    codes: CodeStats[]
    loading?: boolean
    rangeText: string
}) => {
    const [adding, setAdding] = useState(false)
    const [showOld, setShowOld] = useState(false)
    const canEdit = me.status === 'active'
    const trialDays = codes.find((c) => c.trialDays > 0)?.trialDays ?? DEFAULT_TRIAL_DAYS

    const current = codes.filter((c) => c.kind !== 'retired')
    const old = codes.filter((c) => c.kind === 'retired')

    return (
        <Card>
            <Row>
                <CardTitle>Your codes</CardTitle>
                <Spacer />
                {canEdit && !adding && <SecondaryButton onClick={() => setAdding(true)}>Add a code</SecondaryButton>}
            </Row>
            <Muted>
                Use a different code for each place you share to see which one brings buyers. Every code keeps
                crediting you, even after you remove it.
            </Muted>

            {adding && <AddCodeForm trialDays={trialDays} onDone={() => setAdding(false)} />}

            <TableWrap>
                <Table>
                    <thead>
                        <tr>
                            <th>Code</th>
                            <th>Free trial</th>
                            <th className="num">Clicks</th>
                            <th className="num">Signed up</th>
                            <th className="num">Paying</th>
                            <th className="num">Earned</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <EmptyRow colSpan={7}>Loading…</EmptyRow>
                        ) : current.length === 0 ? (
                            <EmptyRow colSpan={7}>No codes yet.</EmptyRow>
                        ) : (
                            current.map((c) => <CodeRow key={c.code} code={c} canEdit={canEdit} trialDays={trialDays} />)
                        )}
                        {showOld && old.map((c) => <CodeRow key={c.code} code={c} canEdit={false} trialDays={trialDays} />)}
                    </tbody>
                </Table>
            </TableWrap>
            <Row>
                <Muted>
                    Clicks and sign-ups are {rangeText}; paying and earned are all-time. A free trial only applies to
                    new customers.
                </Muted>
                <Spacer />
                {old.length > 0 && (
                    <LinkButton onClick={() => setShowOld((v) => !v)}>
                        {showOld ? 'Hide old codes' : `Show old codes (${old.length})`}
                    </LinkButton>
                )}
            </Row>
        </Card>
    )
}

export default CodesCard
